import axios from 'axios';
import {
  EvaluationContextTypeValue,
  EvaluationQuestionTypeValue,
  EvaluationRecipientStatusValue,
  EvaluationRequestStatusValue,
} from '../util/evaluationContract';

export type SupplierSummary = {
  supplier: { _id: string; name: string; isDeleted: boolean };
  score: number | null;
  evaluationCount: number;
  evaluatedProjectsCount: number;
  lastEvaluatedAt: string | null;
  trend: { score: number; submittedAt: string }[];
  byTag: { tagId: string; name: string; archived: boolean; score: number; count: number }[];
  untagged: { score: number | null; count: number };
  comments: {
    responseId: string;
    requestId: string;
    questionText: string;
    score: number | null;
    skipped: boolean;
    comment: string;
    respondentName: string;
    projectName: string | null;
    submittedAt: string;
  }[];
  requests: {
    requestId: string;
    title: string;
    contextType: EvaluationContextTypeValue;
    projectName: string | null;
    projectNumber: string | null;
    sentDate: string;
    dueDate: string;
    status: EvaluationRequestStatusValue;
    sentCount: number;
    answeredCount: number;
    score: number | null;
  }[];
  canHide: boolean;
};

export type ResponseDetail = {
  responseId: string;
  requestId: string;
  title: string;
  supplierName: string;
  projectName: string | null;
  respondent: { name: string; email: string; title: string | null };
  submittedAt: string;
  late: boolean;
  score: number | null;
  hidden: { at: string; reason: string } | null;
  answers: {
    key: string;
    text: string;
    type: EvaluationQuestionTypeValue;
    score: number | null;
    skipped: boolean;
    comment: string | null;
    textAnswer: string | null;
  }[];
  canHide: boolean;
};

export type RequestStatus = {
  request: {
    _id: string;
    title: string;
    supplier: { _id: string; name: string; organizationNumber: string | null };
    context: {
      type: EvaluationContextTypeValue;
      projectId: string | null;
      projectName: string | null;
      projectNumber: string | null;
      areaName: string | null;
    };
    dueDate: string;
    status: EvaluationRequestStatusValue;
    createdAt: string;
    senderName: string | null;
    templateName: string | null;
    closedAt: string | null;
    cancelledAt: string | null;
    completedAt: string | null;
  };
  tiles: {
    recipients: number;
    answered: number;
    declined: number;
    waiting: number;
    failed: number;
    notSent: number;
    overdue: number;
  };
  recipients: {
    _id: string;
    name: string;
    email: string | null;
    title: string | null;
    status: EvaluationRecipientStatusValue;
    sentAt: string | null;
    openedAt: string | null;
    completedAt: string | null;
    declinedAt: string | null;
    responseId: string | null;
    late: boolean;
    score: number | null;
  }[];
  canHide: boolean;
};

// The bare path: axiosHelper rewrites /api/... to /api/organizations/<slug>/....
const BASE_URL = '/api/evaluations';

class ServiceEvaluations {
  async getSupplierSummary(supplierId: string): Promise<SupplierSummary> {
    const response = await axios.get(`${BASE_URL}/suppliers/${supplierId}/summary`);
    return response.data;
  }

  async getResponse(responseId: string): Promise<ResponseDetail> {
    const response = await axios.get(`${BASE_URL}/responses/${responseId}`);
    return response.data;
  }

  async hideResponse(responseId: string, reason: string): Promise<void> {
    await axios.post(`${BASE_URL}/responses/${responseId}/hide`, { reason });
  }

  async getRequestStatus(requestId: string): Promise<RequestStatus> {
    const response = await axios.get(`${BASE_URL}/requests/${requestId}`);
    return response.data;
  }
}

export const serviceEvaluations = new ServiceEvaluations();
